import { findRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, idOf, statusCodeOf, txLog } from './c07.shared'
import { systemTransition } from './opname.shared'

/**
 * Penghubung Claim FG ↔ Putaway FG (v7.50 C-09 / C-10). Dipakai `claimFg.handler` (Proses Claim, tugas simpan lama) dan
 * `fgInbound.handler` (putaway tugas sumber CLAIM) tanpa saling impor.
 *
 * - Tujuan terkunci (K-18): Repack hanya ke pallet Zone Retur Claim yang ditetapkan, Not Good hanya ke pallet karantina
 *   NG FG. Kodenya dibaca dari Master Lokasi (pallet aktif dengan tipe zona `RETURN_CLAIM` / `NG_FG`); kode prototype
 *   (RC-P2, ZN-01-P1) dipakai lebih dulu bila pallet itu ada dan aktif.
 * - Claim ditutup Selesai OTOMATIS (`ACT_CLM_AUTO_COMPLETE`, tanpa aktor manusia) begitu seluruh tugas putaway claim dan
 *   tugas simpan lama (claim sebelum v7.50) selesai.
 */

export type ClaimCategory = 'GOOD' | 'REPACK' | 'NG'

const LOCK: Record<Exclude<ClaimCategory, 'GOOD'>, { preferred: string; zoneType: string; label: string }> = {
  REPACK: { preferred: 'RC-P2', zoneType: 'RETURN_CLAIM', label: 'Zone Retur Claim' },
  NG: { preferred: 'ZN-01-P1', zoneType: 'NG_FG', label: 'Area NG FG' }
}

const zoneTypeOf = (pallet: LocalRow) => String(findRow('warehouse_zone', idOf(pallet.zone_id))?.zone_type ?? '')

/** Pallet tujuan terkunci untuk Repack / Not Good claim. */
export function claimLockTarget(category: 'REPACK' | 'NG'): LocalRow {
  const rule = LOCK[category]
  const pallets = readTable('pallet')
    .filter(row => row.is_active !== false && zoneTypeOf(row) === rule.zoneType)
    .sort((a, b) => String(a.code).localeCompare(String(b.code)))
  const pallet = pallets.find(row => row.code === rule.preferred) ?? pallets[0]
  if (!pallet) fail(`Belum ada pallet aktif di ${rule.label} — atur di Master Lokasi Gudang.`, 'ERR_NO_TARGET', 422)
  return pallet
}

export const claimPutawayTasksOf = (claimId: string) =>
  readTable('fg_putaway_task').filter(task => task.source_type === 'CLAIM' && idOf(task.claim_id) === claimId)

/** Tugas simpan lama (claim sebelum v7.50) yang ikut menentukan Selesai; tugas pengalihan Repack dari pemusnahan tidak. */
export const claimLegacyStoreTasksOf = (claimId: string) =>
  readTable('claim_store_task').filter(task => idOf(task.claim_id) === claimId && task.source !== 'DIVERT')

/** Claim Diproses yang seluruh tugas penyimpanannya selesai → Selesai otomatis. Mengembalikan true bila claim ditutup. */
export function checkClaimComplete(claimId: string): boolean {
  const claim = findRow('claim', claimId)
  if (!claim || statusCodeOf(claim) !== 'CLM_PROCESSING') return false
  const putaway = claimPutawayTasksOf(claimId)
  const store = claimLegacyStoreTasksOf(claimId)
  if (!putaway.length && !store.length) return false
  const done =
    putaway.every(task => task.task_status === 'COMPLETED') && store.every(task => task.task_status === 'DONE')
  if (!done) return false
  systemTransition(
    'WF-CLM-01',
    claim,
    'ACT_CLM_AUTO_COMPLETE',
    'Seluruh barang claim sudah tersimpan — claim selesai otomatis'
  )
  updateRow('claim', claimId, { is_completed: true, completed_at: new Date().toISOString() })
  txLog(
    'FG Claim',
    'CLM-02',
    'clm-selesai-otomatis',
    String(claim.clm_no),
    `Seluruh tugas penyimpanan selesai (${putaway.length} putaway, ${store.length} simpan lama) — claim selesai otomatis`
  )
  return true
}
